import { EditIcon } from '@/react/componentAssets'
import { ThinCheckmark } from '@/react/componentAssets/ThinCheckmark'
import { useAngularServices } from '@/react/components'
import classNames from 'classnames'
import React, { FC, useState, useEffect } from 'react'
import Highlighter from 'react-highlight-words'
import { UISref } from '@components/UISref'
import { CurrentUserType } from '@/react/types/documentation'
import { Project } from '@/react/types/project'
import { IHoverField } from '@/react/types/components'
import HoverFieldPopup from '@/react/screens/components/HoverFieldPopup'

interface ProjectRowProps {
  projectData: Project
  index: number
  handleSelect: (id: number) => void
  selectedItems: number[]
  isActionsAvailable: boolean
  search: string
  page: number
  order: number
  reverse: string
}

export const ProjectRow: FC<ProjectRowProps> = ({
  projectData,
  index,
  handleSelect,
  selectedItems,
  isActionsAvailable,
  search,
  order,
  reverse,
  page,
}) => {
  const { id, name, is_active, number, client_name, users } = projectData

  const { Api, CurrentUser } = useAngularServices()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color

  const [hoveredField, setHoveredField] = useState<IHoverField | null>(null)
  const [isActive, setIsActive] = useState(is_active)

  useEffect(() => {
    setIsActive(is_active)
  }, [is_active])

  const handleToggleActive = async () => {
    try {
      const newActiveState = !isActive
      setIsActive(newActiveState)
      await Api.patch(`projects/${id}`, {
        ids: id.toString(),
        is_active: newActiveState,
      })
    } catch (error) {
      console.log('error', error)
      setIsActive(!isActive)
    }
  }

  return (
    <>
      <tr
        className={classNames('project__row', {
          dark: index % 2 === 0,
        })}
      >
        {isActionsAvailable && (
          <td className="checkmark__td">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={selectedItems.includes(id)}
                onChange={() => handleSelect(id)}
              />
              <span className="checkmark">
                <ThinCheckmark color={variableColor} />
              </span>
            </label>
          </td>
        )}
        <td className="name__td">
          <UISref
            to={`admin.projects.edit`}
            params={{
              projectID: id,
              previousOrder: order,
              previousReverse: reverse,
              previousSearch: search,
              previousPageNumber: page,
            }}
          >
            <div className="title__cell__wrapper">
              <p
                className="table__text title"
                onMouseOver={(e) => {
                  const rect = (e.target as HTMLElement).getBoundingClientRect()
                  setHoveredField({
                    text: name,
                    max__length: 38,
                    position: {
                      top: rect.bottom + window.scrollY,
                      left: rect.left + window.scrollX,
                    },
                  })
                }}
                onMouseOut={() => {
                  setHoveredField(null)
                }}
              >
                <Highlighter
                  highlightClassName={'highlighted__item'}
                  searchWords={[search]}
                  autoEscape={true}
                  textToHighlight={name}
                />
              </p>
            </div>
          </UISref>
        </td>
        <td className="checkmark__td">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => void handleToggleActive()}
            />
            <span className="checkmark">
              <ThinCheckmark color={variableColor} />
            </span>
          </label>
        </td>
        <td
          onMouseOver={(e) => {
            const rect = (e.target as HTMLElement).getBoundingClientRect()
            setHoveredField({
              text: number,
              max__length: 20,
              position: {
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
              },
            })
          }}
          onMouseOut={() => {
            setHoveredField(null)
          }}
        >
          <p className="table__text table-text-number">
            <Highlighter
              highlightClassName={'highlighted__item'}
              searchWords={[search]}
              autoEscape={true}
              textToHighlight={number}
            />
          </p>
        </td>
        <td>
          <p
            className="table__text table-text-client"
            onMouseOver={(e) => {
              const rect = (e.target as HTMLElement).getBoundingClientRect()
              setHoveredField({
                text: client_name,
                max__length: 20,
                position: {
                  top: rect.bottom + window.scrollY,
                  left: rect.left + window.scrollX,
                },
              })
            }}
            onMouseOut={() => {
              setHoveredField(null)
            }}
          >
            <Highlighter
              highlightClassName={'highlighted__item'}
              searchWords={[search]}
              autoEscape={true}
              textToHighlight={client_name}
            />
          </p>
        </td>
        <td className="icon__td">
          <UISref
            to={`admin.projects.editUsers`}
            params={{
              projectID: id,
              previousOrder: order,
              previousReverse: reverse,
              previousSearch: search,
              previousPageNumber: page,
            }}
          >
            <EditIcon color={variableColor} />
          </UISref>
          <p>{users.length}</p>
        </td>
      </tr>
      {hoveredField &&
        hoveredField?.text?.length > hoveredField?.max__length && (
          <HoverFieldPopup hoveredField={hoveredField} />
        )}
    </>
  )
}
